import React, { useState, useEffect } from 'react';
import Button from '@atoms/new/button';
import InteractionWrapper from './InteractionWrapper';
import { usePresentationStore } from '@components/Presentation/store/presentationStore';
import { useSelectedSlide } from '@components/Presentation/hooks/useSelectedSlide';
import Checkbox from '@atoms/Checkbox.new';
import OptionOrderType from '../../Settings/units/OptionOrderType';
import useFonts from '@lib/hooks/useFonts';
import { type SAFonts } from '@atoms/FontPicker';
import { getBarLayout } from '@services/utils.service';
import FileUploader from '@molecules/FileUploader';
import uploadFileToS3 from '@components/RunOfShow/helper/uploadToS3';
import ErrorBanner from '@components/themes/ThemeModal/EditTheme/MenuSettings/units/ErrorBanner';
import { Spinner } from '@atoms/new/spinner';
import ImageLoader from '@atoms/new/imageLoader';
import type { InteractionColors } from '@components/Presentation/types';
import { Vibrant } from 'node-vibrant/browser';
import Textarea from '@atoms/new/textarea';
import useFontSizeCalculator from '@components/Presentation/helpers/fontSizeCalculator';
import { getOptionsGridClasses } from '@components/Features/Quiz/herlper';
import { XIcon as CloseRoundedIcon } from '@phosphor-icons/react';

export const defaultQuizChartColors = ['#4A90E2', '#F5A623', '#FF6F61', '#B54BE2'];

type ImageColors = null | { muted: string; vibrant: string };

export const getQuizImageColors = async (imagePath: string) => {
  try {
    const colors = await Vibrant.from(imagePath).getPalette();
    if (!colors.Muted?.hex && !colors.Vibrant?.hex) return;
    return ({
      muted: colors.Muted!.hex,
      vibrant: colors.Vibrant!.hex
    });
  } catch (error) {
    console.error(error);
  }
};

export const getQuizChartColor = (colorSettings: InteractionColors, index: number) => {
  const multipleColor = defaultQuizChartColors[index % defaultQuizChartColors.length];
  const isMultipleColors = !!colorSettings?.quizMultipleColors;
  const chartColor = isMultipleColors ? multipleColor : (colorSettings?.quizChartColor || '#F97B5F');
  return chartColor;
};

export default function Quiz() {
  const slides = usePresentationStore(s => s.slides);
  const selectedSlideIndex = usePresentationStore(s => s.selectedSlideIndex);
  const selectedSlide = useSelectedSlide();
  const selectedTheme = usePresentationStore(s => s._ext?.selectedTheme);
  const layoutMode = usePresentationStore(s => s.layoutMode);

  let headingColor = selectedSlide?.color?.quizHeadingColor || selectedTheme?.config.default.headingColor || '#FFFFFF';
  let bgColor = selectedSlide?.color?.quizBgColor || selectedTheme?.config.default.bgColor || '#111111';
  let labelColor = selectedSlide?.color?.quizLabelColor || '#FFFFFF';
  let imageSrc = selectedSlide?.setting?.quizImageSrc;
  let imageOpacity = selectedSlide?.setting?.quizImageOpacity;

  // if quiz doesnt exist in theme use global config
  if (!('quizImageSrc' in selectedSlide.setting)) {
    imageSrc = selectedTheme?.config.default.imageSrc;
    imageOpacity = selectedTheme?.config.default.imageOpacity;
  }

  const font = selectedSlide.setting.FontStyle as SAFonts || 'Sans-Serif';
  const { getFontStyle } = useFonts();
  const fontStyle = getFontStyle(font);

  const [fontSize, setFontSize] = useState('text-4xl');
  const [roundedCorner, setRoundedCorner] = useState('rounded-[40px]');
  const [parentContainer, setParentContainer] = useState({ height: 'h-full' });
  const [isLoading, setIsLoading] = useState(false);
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [customErrMessage, setCustomErrMessage] = useState('');
  const [showFileUploader, setShowFileUploader] = useState(true);
  const [imgColors, setImgColors] = useState<ImageColors>(null);

  const slideImage = selectedSlide.setting?.quizSlideImageSrc;

  const slideImagePath = `${process.env.NEXT_PUBLIC_BUCKET_URL}${slideImage}`;

  const getFontSizeClass = (length: number) => {
    if (length < 10) return 'text-3xl';
    if (length < 30) return 'text-2xl';
    if (length < 50) return 'text-xl';
    return 'text-md';
  };

  useEffect(() => {
    const length = slides[selectedSlideIndex]?.options?.length || 0;

    const {
      fontSize,
      roundedCorner,
      parentContainer,
    } = getBarLayout(length, true);

    setFontSize(fontSize);
    setRoundedCorner(roundedCorner);
    setParentContainer(parentContainer);
  }, [slides]);

  useEffect(() => {
    if (!slideImage) return;
    getQuizImageColors(slideImagePath).then(colors => {
      if (!colors) return;
      setImgColors(colors);
    });
    setShowFileUploader(true);
  }, [slideImage]);

  const uploadFileToS3andSave = async (fileToUpload: any) => {
    setIsLoading(true);
    try {
      const { name } = fileToUpload[0];
      const extension = name.split('.').slice(-1)[0];
      const fileNameWithoutExtension = name.replace(`.${extension}`, '').replace(/\s+/g, '_');
      const fileKey = `Theme-Images/${fileNameWithoutExtension}_${Date.now()}.${extension}`;

      await uploadFileToS3(fileToUpload[0], fileKey, setUploadPercentage);

      selectedSlide.handler.updateSetting('quizSlideImageSrc', fileKey);
    } catch (error) {
      setUploadPercentage(0);
    }
    setIsLoading(false);
  };

  const computedFontSize = useFontSizeCalculator(
    selectedSlide.setting.quizFontSize ?
      Number(selectedSlide.setting.quizFontSize) : 1, 1.8, 'rem');

  return (
    <InteractionWrapper
      bgColor={bgColor}
      title={selectedSlide.title}
      updateTitle={(e) => selectedSlide.handler.updateTitle(e)}
      headingColor={headingColor}
      bgImg={imageSrc}
      bgImgOpacity={imageOpacity}
    >
      <div className={`flex ${layoutMode === 'square' ? 'flex-col' : ''} size-full gap-4 `}>
        {showFileUploader && <div
          style={{
            backgroundImage: slideImage ? `radial-gradient(circle, ${imgColors?.vibrant}, ${imgColors?.muted})` : undefined,
          }}
          className={`relative ${roundedCorner} ${slideImage ? '' : 'border-3  bg-grey-50'} ${layoutMode === 'square' ? 'h-2/5 w-[98%] mx-auto' : 'h-full w-2/5'} flex items-center justify-center relative border-dashed border-grey-400`}>
          <Button
            className='absolute -top-2 -right-2 rounded-full z-10 '
            kind='secondary'
            tooltip={{
              content: 'Remove image'
            }}
            isIconOnly
            onPress={() => {
              selectedSlide.handler.updateSetting('quizSlideImageSrc', null);
              setShowFileUploader(false);
            }}
          >
            <CloseRoundedIcon />
          </Button>
          {!isLoading && !slideImage && <>
            <ErrorBanner errMsg={customErrMessage} />
          </>}
          <FileUploader
            accept={{
              'image/png': ['.png'],
              'image/jpeg': ['.jpg', '.jpeg'],
              'image/webp': ['.webp'],
              // @ts-expect-error js component
              'image/gif': ['.gif'],
            }}
            element={
              <div className={`absolute overflow-hidden left-0 ${(!isLoading && !slideImage) ? 'size-full top-0' : 'bottom-0 w-full rounded-b-[20px]'}`}>
                {(!isLoading && !slideImage) ? (<div className='absolute size-full top-0 left-0  flex flex-col items-center justify-center gap-2'>
                  <ImageLoader src='/images/empty_img.svg' className='size-8' />
                  <Button
                    kind='secondary'
                    isLoading={isLoading}
                    className='pointer-events-none'
                  >
                    Upload here
                  </Button>
                  <p>or</p>
                  <p>drag here to upload</p>
                </div>) :
                  <div className='cursor-pointer'>
                    <Button
                      kind='secondary'
                      className='w-full pointer-events-none cursor-pointer'
                    >
                      Change Image
                    </Button>
                  </div>}
              </div>
            }
            uploadFileToS3andSave={uploadFileToS3andSave}
            showProgress={false}
            maxSize={10485760}
            maxSizeErrorMsg='Maximum size allowed is 10MB.'
            isUploadInProgress={false}
            setIsUploadInProgress={() => { }}
            updateCustomErrorMessage={(msg: string) => setCustomErrMessage(msg)}
            showDefaultErrorMessage={false}
          />
          {!isLoading && !!slideImage && <ImageLoader
            src={slideImagePath}
            className={`size-full object-contain ${roundedCorner} bg-transparent`}
            alt='quiz image' />}
          {isLoading && <Spinner />}
        </div>}
        <div className={`w-full grid gap-4 h-full justify-center ${getOptionsGridClasses({
          optionCount: selectedSlide?.options?.length || 0,
          hasImage: showFileUploader,
          layoutMode,
        })}`}>

          {selectedSlide?.options?.map((option, index) => (
            <div
              key={option.id || index}
              className={`${parentContainer.height} flex flex-1 gap-2 relative`}
            >

              <div
                style={{
                  background: getQuizChartColor(selectedSlide.color, index),
                  fillOpacity: 0
                }}
                className={`w-full h-full ${roundedCorner} flex items-center bg-opacity-100 left-0 m-auto flex-1 relative overflow-hidden`}>
                <Checkbox
                  isCircular
                  checked={!!option.checked}
                  size='2xl'
                  variant='success'
                  className='absolute z-10 right-4 top-4'
                  onChange={() => {
                    if (option.checked) return;
                    selectedSlide.handler.updateQuizOptions(index, !option.checked);
                  }} />
                <div className="bg-black/10 ml-4 rounded-full w-12 h-12 flex items-center justify-center">
                  <OptionOrderType
                    style={{
                      color: labelColor,
                      fontFamily: fontStyle?.fontFamily,
                      fontSize: computedFontSize
                    }}
                    className={`${fontSize} w-16 min-h-0 grid place-items-center`}
                    index={index}
                    type={selectedSlide.setting.optionOrderType}
                    prefix={selectedSlide.setting.prefix} />
                </div>
                <div className={' flex-1 h-full flex left-0 right-0 m-0 overflow-hidden'}>
                  <Textarea
                    style={{ fontFamily: fontStyle?.fontFamily, color: labelColor, fontSize: `${((option.body?.length || 0) < 100 ? (showFileUploader && layoutMode !== 'square' ? 24 : 28) : (showFileUploader && layoutMode !== 'square' ? 18 : 24)) * (Number(selectedSlide.setting.quizFontSize) || 1)}px` }}
                    value={option.body}
                    onChange={(e) => selectedSlide.handler.updateOptions(index, e)}
                    placeholder={`Option ${index + 1}`}
                    maxLength={150}
                    classNames={{
                      inputWrapper: 'border-0 h-full shadow-none !min-h-full overflow-hidden',
                      input: `my-auto !leading-tight ${getFontSizeClass(option.body?.length)}`,
                    }}
                    minRows={1}
                    maxRows={10}
                    size="lg"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

    </InteractionWrapper>
  );
}
